import { decamelize } from 'humps';
import { api } from './index';
import type { ClinicalAuthor } from '../utils/clinical-author';

const BASE = '/api/internal';

export type SuppliedBy = 'company' | 'patient';
export type PeriodStatus = 'upcoming' | 'in_force' | 'ended' | 'entered_in_error';
export type OrderListStatus = 'in_force' | 'upcoming' | 'history';

export interface MedicationDosage {
  id: number;
  doseQuantity: number;
  timesOfDay: number[];
  weekdays: number[];
  asNeeded: boolean;
  maxDosesPer24h: number | null;
  singleDose: boolean;
  indication: string | null;
  instructions: string | null;
  summary: string;
}

export type AllergySeverity = 'unknown' | 'mild' | 'severe';

// A patient's allergy, to a catalog substance (checked against the drugs of
// new orders) or in words. A `none_known` row says the patient has no known
// allergies; with no row at all, nobody asked.
export interface PatientAllergy {
  id: number;
  kind: 'allergy' | 'none_known';
  substanceId: number | null;
  // The substance, or the words.
  name: string | null;
  description: string | null;
  reaction: string | null;
  severity: AllergySeverity;
  humanSeverity: string;
  author: ClinicalAuthor | null;
  humanCreatedAt: string;
}

export interface AllergyForm {
  kind?: PatientAllergy['kind'];
  substanceId?: number | null;
  description?: string;
  reaction?: string;
  severity?: AllergySeverity;
}

export interface MedicationOrderHold {
  id: number;
  heldFrom: string;
  heldUntil: string | null;
  reason: string | null;
}

export interface MedicationOrder {
  id: number;
  patientId: number;
  clinicalDrugId: number;
  // Active ingredients of the drug, to warn about the same one twice.
  substanceIds: number[];
  drugName: string;
  doseUnit: string;
  doseUnitLabel: string;
  highAlert: boolean;
  route: string;
  routeLabel: string;
  state: 'active' | 'stopped' | 'entered_in_error';
  humanState: string;
  suppliedBy: SuppliedBy;
  humanSuppliedBy: string;
  startsAt: string;
  endsAt: string | null;
  // "dd-mm-yyyy HH:MM" in the company time zone.
  humanStartsAt: string;
  humanEndsAt: string | null;
  retroactive: boolean;
  periodStatus: PeriodStatus;
  humanPeriodStatus: string;
  stopReason: string | null;
  stoppedBy: string | null;
  prescriberName: string | null;
  prescriberIdentifier: string | null;
  prescribedOn: string | null;
  prescriptionDocumentUrl: string | null;
  transcribedBy: string;
  // Who transcribed it, who stopped it and who voided it, as they were then.
  author: ClinicalAuthor | null;
  humanCreatedAt: string;
  stopper: ClinicalAuthor | null;
  voidReason: string | null;
  voider: ClinicalAuthor | null;
  humanVoidedAt: string | null;
  replacesId: number | null;
  replacementId: number | null;
  onHold: boolean;
  createdAt: string;
  dosages: MedicationDosage[];
  holds: MedicationOrderHold[];
}

// One dosage instruction as the form edits it. Times are minutes after midnight.
export interface DosageForm {
  doseQuantity: number | null;
  kind: 'scheduled' | 'as_needed' | 'single';
  timesOfDay: number[];
  // false: only on `weekdays` ("Algunos días").
  everyDay: boolean;
  weekdays: number[];
  maxDosesPer24h: number | null;
  indication: string;
  instructions: string;
}

export interface MedicationOrderForm {
  clinicalDrugId?: number;
  route: string;
  suppliedBy: SuppliedBy;
  // Local date-times ("2026-10-10T08:00"), read in the company time zone.
  startsAt: string;
  endsAt: string;
  retroactive: boolean;
  prescriberName: string;
  prescriberIdentifier: string;
  prescribedOn: string;
  prescriptionDocument: File | null;
  dosages: DosageForm[];
}

function dosageAttributes(dosage: DosageForm) {
  return {
    doseQuantity: dosage.doseQuantity,
    timesOfDay: dosage.kind === 'scheduled' ? dosage.timesOfDay : [],
    weekdays: dosage.kind === 'scheduled' && !dosage.everyDay ? dosage.weekdays : [],
    asNeeded: dosage.kind === 'as_needed',
    singleDose: dosage.kind === 'single',
    maxDosesPer24h: dosage.kind === 'as_needed' ? dosage.maxDosesPer24h : null,
    indication: dosage.kind === 'as_needed' ? dosage.indication : '',
    instructions: dosage.instructions,
  };
}

// humps leaves digits glued: maxDosesPer24h → max_doses_per24h.
function railsKey(key: string): string {
  return decamelize(key).replace(/per(\d)/, 'per_$1');
}

function append(data: FormData, key: string, value: unknown) {
  if (Array.isArray(value)) {
    // An empty list is left out: the server defaults it to [].
    value.forEach((item) => data.append(`${key}[]`, String(item)));
  } else if (value instanceof File) {
    data.append(key, value);
  } else if (value !== null && value !== undefined) {
    data.append(key, String(value));
  }
}

// Multipart so the scanned prescription travels with the order. Keys are
// written in snake_case here, which the case converter leaves untouched.
export function orderFormData(form: MedicationOrderForm, reason?: string): FormData {
  const data = new FormData();
  const order = {
    clinicalDrugId: form.clinicalDrugId,
    route: form.route,
    suppliedBy: form.suppliedBy,
    startsAt: form.startsAt,
    endsAt: form.endsAt,
    retroactive: form.retroactive,
    prescriberName: form.prescriberName,
    prescriberIdentifier: form.prescriberIdentifier,
    prescribedOn: form.prescribedOn,
    prescriptionDocument: form.prescriptionDocument,
  };
  Object.entries(order).forEach(([key, value]) => append(data, `medication_order[${railsKey(key)}]`, value));
  form.dosages.forEach((dosage, index) => {
    Object.entries(dosageAttributes(dosage)).forEach(([key, value]) => (
      append(data, `medication_order[dosages_attributes][${index}][${railsKey(key)}]`, value)
    ));
  });
  if (reason) data.append('reason', reason);

  return data;
}

export type AdministrationState = 'completed' | 'not_done' | 'entered_in_error';
export type StatusReason = 'refused' | 'held' | 'unavailable' | 'omitted';

export interface MedicationAdministration {
  id: number;
  dosageId: number;
  // As the order was transcribed.
  drugName: string;
  scheduledAt: string | null;
  state: AdministrationState;
  statusReason: StatusReason | null;
  humanStatus: string;
  reason: string | null;
  notes: string | null;
  givenAt: string;
  // "HH:MM" in the company time zone.
  givenTime: string;
  quantity: number | null;
  extraDose: boolean;
  offSchedule: boolean;
  consumption: 'none' | 'posted' | 'failed' | 'reversed';
  consumptionError: string | null;
  source: 'mobile' | 'office' | 'system';
  // The Ejecutor ("Dio") and the Autor ("Registró"), as they were then.
  performer: string | null;
  recordedBy: string | null;
  author: ClinicalAuthor | null;
  correctsId: number | null;
  createdAt: string;
}

export interface DoseDrug {
  orderId: number;
  dosageId: number;
  drugName: string;
  highAlert: boolean;
  routeLabel: string;
  doseQuantity: number;
  doseUnitLabel: string;
  consumptionPolicy: 'per_dose' | 'per_container';
  instructions: string | null;
  summary: string;
}

export interface DueDose extends DoseDrug {
  scheduledAt: string;
  scheduledTime: string;
  // A shift covers it; without one it is never marked omitted.
  covered: boolean;
  status: 'due' | 'recorded';
  administration: MedicationAdministration | null;
}

export interface AsNeededMedication extends DoseDrug {
  indication: string | null;
  maxDosesPer24h: number | null;
  givenLast24h: number;
}

export interface MedicationDoses {
  from: string;
  to: string;
  doses: DueDose[];
  asNeeded: AsNeededMedication[];
  unscheduled: MedicationAdministration[];
}

export interface AdministrationForm {
  medicationDosageId: number;
  performerCaregiverId?: number;
  scheduledAt?: string;
  status: 'completed' | 'not_done';
  statusReason?: StatusReason;
  reason?: string;
  givenAt?: string;
  quantity?: number;
  extraDose?: boolean;
  openedNewContainer?: boolean;
  notes?: string;
}

const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };

export const medicationsApi = {
  orders(patientId: number, status: OrderListStatus = 'in_force'): Promise<MedicationOrder[]> {
    return api.get(`${BASE}/patients/${patientId}/medication_orders?status=${status}`)
      .then((r) => r.data.medicationOrders);
  },
  create(patientId: number, form: MedicationOrderForm): Promise<MedicationOrder> {
    return api.post(`${BASE}/patients/${patientId}/medication_orders`, orderFormData(form), multipart)
      .then((r) => r.data.medicationOrder);
  },
  replace(id: number, form: MedicationOrderForm, reason?: string): Promise<MedicationOrder> {
    return api.post(`${BASE}/medication_orders/${id}/replace`, orderFormData(form, reason), multipart)
      .then((r) => r.data.medicationOrder);
  },
  stop(id: number, attrs: { reason?: string; at?: string }): Promise<MedicationOrder> {
    return api.put(`${BASE}/medication_orders/${id}/stop`, attrs).then((r) => r.data.medicationOrder);
  },
  void(id: number, reason: string): Promise<MedicationOrder> {
    return api.put(`${BASE}/medication_orders/${id}/void`, { reason }).then((r) => r.data.medicationOrder);
  },
  hold(id: number, attrs: { all?: boolean; from?: string; to?: string; reason?: string }):
  Promise<MedicationOrder> {
    return api.post(`${BASE}/medication_orders/${id}/hold`, attrs).then((r) => r.data.medicationOrder);
  },
  releaseHold(id: number): Promise<MedicationOrder> {
    return api.put(`${BASE}/medication_orders/${id}/release_hold`).then((r) => r.data.medicationOrder);
  },

  // The doses of a patient for a day (YYYY-MM-DD, company time zone).
  doses(patientId: number, date: string): Promise<MedicationDoses> {
    return api.get(`${BASE}/patients/${patientId}/medication_administrations?date=${date}`)
      .then((r) => r.data.medicationDoses);
  },
  // The doses due from the shift's start to its end, and those given without a slot.
  shiftDoses(patientId: number, shiftId: number): Promise<MedicationDoses> {
    return api.get(`${BASE}/patients/${patientId}/medication_administrations?shift_id=${shiftId}`)
      .then((r) => r.data.medicationDoses);
  },
  recordAdministration(patientId: number, form: AdministrationForm): Promise<MedicationAdministration> {
    const { medicationDosageId, performerCaregiverId, ...administration } = form;

    return api.post(`${BASE}/patients/${patientId}/medication_administrations`, {
      medicationDosageId, performerCaregiverId, medicationAdministration: administration,
    }).then((r) => r.data.medicationAdministration);
  },
  voidAdministration(id: number, reason: string): Promise<MedicationAdministration> {
    return api.put(`${BASE}/medication_administrations/${id}/void`, { reason })
      .then((r) => r.data.medicationAdministration);
  },

  // The patient's kept allergies, or their "sin alergias conocidas".
  allergies(patientId: number): Promise<PatientAllergy[]> {
    return api.get(`${BASE}/patients/${patientId}/allergies`).then((r) => r.data.patientAllergies);
  },
  recordAllergy(patientId: number, allergy: AllergyForm): Promise<PatientAllergy> {
    return api.post(`${BASE}/patients/${patientId}/allergies`, { allergy })
      .then((r) => r.data.patientAllergy);
  },
  updateAllergy(patientId: number, id: number, allergy: AllergyForm): Promise<PatientAllergy> {
    return api.put(`${BASE}/patients/${patientId}/allergies/${id}`, { allergy })
      .then((r) => r.data.patientAllergy);
  },
  removeAllergy(patientId: number, id: number): Promise<void> {
    return api.delete(`${BASE}/patients/${patientId}/allergies/${id}`).then(() => undefined);
  },
};
